import { VehicleRemovalCodes } from '@policyfly/protobuf'
import {
  VehicleRemovalCode as ScheduleVehicleRemovalCode,
  VehicleType as ScheduleVehicleType,
} from '@policyfly/protobuf/pfschedule'
import {
  Formatter,
  SchemaSettings_ProgramData_Field_Schedule_Config,
} from '@policyfly/protobuf/pfschema'
import { formatSentence } from '@policyfly/utils/string'
import Big from 'big.js'

import Payload, { PayloadArray } from '@/lib/Payload'
import { basicFormatter, formatDate, formatValue } from '@/utils/formatter'
import { vehicleTypeToVinLookupTypeMap } from '@/utils/protobuf/mappers'

import type { ChangeList, ChangeListItem, Vehicle, VehicleType } from '@policyfly/protobuf'

interface StrictChangeListItem<T extends string, U extends object> extends Omit<ChangeListItem, 'values' | 'previousValues'> {
  itemType: T
  values: Partial<Record<keyof U, string>>
  previousValues: Partial<Record<keyof U, string>>
}
export type ValidChangeListItem =
  | StrictChangeListItem<'VEHICLE', Vehicle>

type ChangeFields<T extends object> = {
  name: string
  path: keyof T
  formatter: Formatter
}

export interface ChangeListConfig {
  removalReasonsMap: Record<string | number, string>
  vehicleChanges: ChangeFields<Vehicle>[]
  unspecifiedValue: string
}

// Copied from `snippets/gamp/common` in gamp-config
const removalReasonsMap = {
  [VehicleRemovalCodes.CHANGE_AMOUNT_INSURED_VEHICLE_REMOVAL]: 'Change Amount Insured',
  [VehicleRemovalCodes.ERRONEOUS_ADDITION_VEHICLE_REMOVAL]: 'Added in error',
  [VehicleRemovalCodes.LEASE_TERMINATION_VEHICLE_REMOVAL]: 'Lease terminated',
  [VehicleRemovalCodes.SOLD_UNIT_VEHICLE_REMOVAL]: 'Sold unit',
  [VehicleRemovalCodes.TOTAL_LOSS_VEHICLE_REMOVAL]: 'Total loss',
  [VehicleRemovalCodes.NOT_ADVISED_VEHICLE_REMOVAL]: 'Not advised',
  [VehicleRemovalCodes.MECHANICAL_FAULT_VEHICLE_REMOVAL]: 'Down for repair',
  [VehicleRemovalCodes.OUT_OF_SERVICE_VEHICLE_REMOVAL]: 'Out of service',
  [VehicleRemovalCodes.COVERAGE_NOT_NEEDED_VEHICLE_REMOVAL]: 'Coverage no longer needed',
  [VehicleRemovalCodes.OTHER_VEHICLE_REMOVAL]: 'Other',
}

// Friendly display names for vehicle types
export const vehicleTypeToVinLookupTypeRecord: Record<VehicleType, string> = Object.fromEntries(
  new Map(Array.from(vehicleTypeToVinLookupTypeMap).map(([type, name]) => [type, formatSentence(name)])),
) as Record<VehicleType, string>

const vehicleChanges: ChangeFields<Vehicle>[] = [
  { name: 'VIN', path: 'vin', formatter: basicFormatter('default') },
  { name: 'Amount', path: 'insuredValue', formatter: basicFormatter('currency') },
  { name: 'Type', path: 'type', formatter: Formatter.create({ name: { oneofKind: 'mapValue', mapValue: { valueMap: vehicleTypeToVinLookupTypeRecord } } }) },
  { name: 'Year', path: 'year', formatter: basicFormatter('default') },
  { name: 'Make', path: 'make', formatter: basicFormatter('default') },
  { name: 'Model', path: 'model', formatter: basicFormatter('default') },
  { name: 'Add Date', path: 'added', formatter: basicFormatter('date') },
  { name: 'Deductible', path: 'replacementValue', formatter: basicFormatter('currency') },
  { name: 'Total Loss', path: 'actualCashValue', formatter: basicFormatter('currency') },
  { name: 'Removal Date', path: 'removed', formatter: basicFormatter('date') },
]

const defaultConfig: ChangeListConfig = {
  removalReasonsMap,
  vehicleChanges,
  unspecifiedValue: '0',
}

// Schedule-compatible maps for pfschedule enum string names
const scheduleRemovalReasonsMap: Record<string, string> = {
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_CHANGE_AMOUNT_INSURED]]: 'Change Amount Insured',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_ERRONEOUS_ADDITION]]: 'Added in error',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_LEASE_TERMINATION]]: 'Lease terminated',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_SOLD_UNIT]]: 'Sold unit',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_TOTAL_LOSS]]: 'Total loss',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_NOT_ADVISED]]: 'Not advised',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_MECHANICAL_FAULT]]: 'Down for repair',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_OUT_OF_SERVICE]]: 'Out of service',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_COVERAGE_NOT_NEEDED]]: 'Coverage no longer needed',
  [ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_OTHER]]: 'Other',
}

export const scheduleVehicleTypeRecord: Record<string, string> = {
  [ScheduleVehicleType[ScheduleVehicleType.VEHICLE_TYPE_TRUCK]]: 'Truck',
  [ScheduleVehicleType[ScheduleVehicleType.VEHICLE_TYPE_TRACTOR]]: 'Tractor',
  [ScheduleVehicleType[ScheduleVehicleType.VEHICLE_TYPE_TRAILER]]: 'Trailer',
  [ScheduleVehicleType[ScheduleVehicleType.VEHICLE_TYPE_BUS]]: 'Bus',
  [ScheduleVehicleType[ScheduleVehicleType.VEHICLE_TYPE_PASSENGER_CAR]]: 'Passenger Car',
}

const scheduleVehicleChanges: ChangeFields<Vehicle>[] = [
  { name: 'VIN', path: 'vin', formatter: basicFormatter('default') },
  { name: 'Amount', path: 'insuredValue', formatter: basicFormatter('currency') },
  { name: 'Type', path: 'type', formatter: Formatter.create({ name: { oneofKind: 'mapValue', mapValue: { valueMap: scheduleVehicleTypeRecord } } }) },
  { name: 'Year', path: 'year', formatter: basicFormatter('default') },
  { name: 'Make', path: 'make', formatter: basicFormatter('default') },
  { name: 'Model', path: 'model', formatter: basicFormatter('default') },
  { name: 'Add Date', path: 'added', formatter: basicFormatter('date') },
  { name: 'Deductible', path: 'replacementValue', formatter: basicFormatter('currency') },
  { name: 'Total Loss', path: 'actualCashValue', formatter: basicFormatter('currency') },
  { name: 'Removal Date', path: 'removed', formatter: basicFormatter('date') },
]

export const scheduleChangeListConfig: ChangeListConfig = {
  removalReasonsMap: scheduleRemovalReasonsMap,
  vehicleChanges: scheduleVehicleChanges,
  unspecifiedValue: ScheduleVehicleRemovalCode[ScheduleVehicleRemovalCode.VEHICLE_REMOVAL_CODE_UNSPECIFIED],
}

enum ScheduleDiffState {
  ADDED = 'Added',
  REMOVED = 'Removed',
  CHANGED = 'Changed'
}

/**
 * Generates a text summary for the passed in {@link ChangeListItem}.
 * The `itemType` affects what displays in the summary.
 * Pass a {@link ChangeListConfig} to customize the maps and guard values (e.g. for pfschedule compatibility).
 */
export function generateItemSummary (item: ValidChangeListItem, type?: keyof Omit<ChangeList, 'title'>, config: ChangeListConfig = defaultConfig): string {
  const parts: string[] = []
  switch (item.itemType) {
    case 'VEHICLE': {
      const vehicle = item.values
      parts.push(vehicle.vin ?? '')
      // changed items only display changed fields
      if (type === 'changed') {
        // only include insured value for APD
        if (item.values.insuredValue) parts.push(`${formatValue(item.values.insuredValue, basicFormatter('currency'))}`)
        // check if fields marked for display had values previously and display them
        const changes: string[] = []
        config.vehicleChanges.forEach(({ path, name, formatter }) => {
          const value = item.values[path]
          const previous = item.previousValues[path]
          if (previous) changes.push(`${name}: ${formatValue(previous, formatter)} to ${formatValue(value, formatter)}`)
        })
        if (changes.length) parts.push(`(${changes.join(',  ')})`)
      } else {
        const { removed, added, insuredValue, removedReason, removedReasonDetail } = vehicle
        if (removed) parts.push(`Removed: ${formatDate(removed, { year: '2-digit', month: '2-digit', day: '2-digit' })}`)
        if (added) parts.push(`Added: ${formatDate(added, { year: '2-digit', month: '2-digit', day: '2-digit' })}`)
        if (Big(insuredValue || 0).gt(0)) parts.push(formatValue(insuredValue, basicFormatter('currency')))
        if (removedReason && removedReason !== config.unspecifiedValue) {
          const formattedReason = formatValue(removedReason, Formatter.create({ name: { oneofKind: 'mapValue', mapValue: { valueMap: config.removalReasonsMap } } }))
          parts.push(`Reason: ${removedReasonDetail || formattedReason}`)
        }
      }
      return parts.join('   ')
    }
    default:
      return ''
  }
}

/**
 * Converts the passed in {@link ChangeList} into a {@link PayloadArray}.
 * This is designed for use within `programData` to generate endorsement summaries.
 *
 * Pass a {@link SchemaSettings_ProgramData_Field_Schedule_Config Config} to customize the maps
 * and guard values (e.g. for pfschedule compatibility).
 */
export function convertChangeList (
  changes: ChangeList | null | undefined,
  configType: SchemaSettings_ProgramData_Field_Schedule_Config | undefined,
): PayloadArray {
  let config: ChangeListConfig = defaultConfig
  switch (configType) {
    case SchemaSettings_ProgramData_Field_Schedule_Config.CONFIG_SCHEDULE_VEHICLE:
      config = scheduleChangeListConfig
      break
  }

  const basePayload = new Payload()
  basePayload.createResponse('state', null)
  basePayload.createResponse('summary', '')
  const payloadArray = new PayloadArray(basePayload)
  if (!changes) return payloadArray
  changes.added.forEach((item) => {
    const newItem = payloadArray.add()
    newItem.set('state', ScheduleDiffState.ADDED)
    newItem.set('summary', generateItemSummary(item as ValidChangeListItem, 'added', config))
  })
  changes.changed.forEach((change) => {
    const newItem = payloadArray.add()
    newItem.set('state', ScheduleDiffState.CHANGED)
    newItem.set('summary', generateItemSummary(change as ValidChangeListItem, 'changed', config))
  })
  if (changes.removed) {
    changes.removed.forEach((item) => {
      const newItem = payloadArray.add()
      newItem.set('state', ScheduleDiffState.REMOVED)
      newItem.set('summary', generateItemSummary(item as ValidChangeListItem, 'removed', config))
    })
  }
  return payloadArray
}
